/**
 * ============================================================================
 * OFFLINE INDICATOR v1.0.0 - PWA STATUS ANZEIGE
 * ============================================================================
 *
 * Zeigt den Offline/Online Status an:
 * - Roter Banner wenn offline
 * - Sync-Status wenn Änderungen ausstehen
 * - Manueller Sync-Button
 *
 * @version 1.0.0
 * @date 2026-01-16
 */

import React, { useState } from 'react';
import { useOffline } from '../../context/OfflineContext';
import { useTheme } from '../../context/ThemeContext';

console.log('📶 OfflineIndicator.jsx v1.0.0 loaded');

/**
 * OfflineIndicator Component
 * Wird in App.js eingebunden, erscheint am oberen Bildschirmrand
 */
function OfflineIndicator() {
  const { isDark } = useTheme();
  const {
    isOnline,
    pendingSyncCount,
    isSyncing,
    syncError,
    triggerSync,
    formatLastSyncTime
  } = useOffline();

  const [isExpanded, setIsExpanded] = useState(false);

  // Nichts anzeigen wenn online und keine ausstehenden Syncs
  if (isOnline && pendingSyncCount === 0 && !syncError) {
    return null;
  }

  const styles = getStyles(isDark, isOnline);

  const handleSyncClick = async () => {
    if (!isSyncing && isOnline) {
      await triggerSync();
    }
  };

  return (
    <div style={styles.container}>
      {/* Main Banner */}
      <div
        style={styles.banner}
        onClick={() => setIsExpanded(!isExpanded)}
        role="status"
        aria-live="polite"
      >
        <div style={styles.content}>
          {/* Icon */}
          <span style={styles.icon}>
            {!isOnline ? '📴' : isSyncing ? '🔄' : '⏳'}
          </span>

          {/* Message */}
          <span style={styles.message}>
            {!isOnline ? (
              'Offline-Modus - Änderungen werden lokal gespeichert'
            ) : isSyncing ? (
              'Synchronisiere...'
            ) : (
              `${pendingSyncCount} Änderung${pendingSyncCount !== 1 ? 'en' : ''} warten auf Sync`
            )}
          </span>

          {/* Actions */}
          <div style={styles.actions}>
            {/* Sync Button (nur wenn online und nicht bereits syncing) */}
            {isOnline && !isSyncing && pendingSyncCount > 0 && (
              <button
                style={styles.syncButton}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSyncClick();
                }}
                aria-label="Jetzt synchronisieren"
              >
                Sync
              </button>
            )}

            {/* Expand Arrow */}
            <span style={{
              ...styles.expandArrow,
              transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
            }}>
              ▼
            </span>
          </div>
        </div>

        {/* Loading Bar */}
        {isSyncing && (
          <div style={styles.loadingBar}>
            <div style={styles.loadingBarProgress} />
          </div>
        )}
      </div>

      {/* Expanded Details */}
      {isExpanded && (
        <div style={styles.details}>
          {/* Status Info */}
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Status:</span>
            <span style={{
              ...styles.detailValue,
              color: isOnline ? '#22C55E' : '#EF4444'
            }}>
              {isOnline ? 'Online' : 'Offline'}
            </span>
          </div>

          {/* Pending Count */}
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Ausstehend:</span>
            <span style={styles.detailValue}>
              {pendingSyncCount} Änderung{pendingSyncCount !== 1 ? 'en' : ''}
            </span>
          </div>

          {/* Last Sync */}
          <div style={styles.detailRow}>
            <span style={styles.detailLabel}>Letzter Sync:</span>
            <span style={styles.detailValue}>
              {formatLastSyncTime()}
            </span>
          </div>

          {/* Error Message */}
          {syncError && (
            <div style={styles.errorRow}>
              <span style={styles.errorIcon}>⚠️</span>
              <span style={styles.errorMessage}>{syncError}</span>
            </div>
          )}

          {/* Hint */}
          <div style={styles.hint}>
            {!isOnline ? (
              'Ihre Änderungen werden automatisch synchronisiert, sobald Sie wieder online sind.'
            ) : (
              'Klicken Sie auf "Sync" um die Änderungen jetzt zu synchronisieren.'
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Styles
 */
function getStyles(isDark, isOnline) {
  const colors = {
    // Offline: Rot/Orange Gradient
    offlineBg: 'linear-gradient(90deg, #EF4444, #F59E0B)',
    offlineText: '#FFFFFF',

    // Online mit Pending: Planivio Teal
    pendingBg: isDark ? '#1E3A4A' : '#E8F4F8',
    pendingBorder: '#4A90A4',
    pendingText: isDark ? '#F9FAFB' : '#1F2937',

    // Common
    buttonBg: 'rgba(255, 255, 255, 0.2)',
    buttonHover: 'rgba(255, 255, 255, 0.3)',
    detailsBg: isDark ? '#1F2937' : '#FFFFFF',
    detailsBorder: isDark ? '#4B5563' : '#E5E7EB'
  };

  return {
    container: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 9999,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    },

    banner: {
      background: !isOnline ? colors.offlineBg : colors.pendingBg,
      border: isOnline ? `1px solid ${colors.pendingBorder}` : 'none',
      color: !isOnline ? colors.offlineText : colors.pendingText,
      cursor: 'pointer',
      transition: 'all 0.3s ease'
    },

    content: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '10px',
      padding: '10px 16px',
      minHeight: '40px'
    },

    icon: {
      fontSize: '1.1rem',
      animation: !isOnline ? 'none' : 'spin 1s linear infinite'
    },

    message: {
      fontSize: '0.875rem',
      fontWeight: 500,
      flex: 1,
      textAlign: 'center'
    },

    actions: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },

    syncButton: {
      background: !isOnline ? colors.buttonBg : '#4A90A4',
      color: 'white',
      border: 'none',
      padding: '6px 14px',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      whiteSpace: 'nowrap'
    },

    expandArrow: {
      fontSize: '0.7rem',
      transition: 'transform 0.2s ease',
      opacity: 0.8
    },

    loadingBar: {
      height: '3px',
      background: 'rgba(255, 255, 255, 0.3)',
      overflow: 'hidden'
    },

    loadingBarProgress: {
      height: '100%',
      width: '30%',
      background: 'rgba(255, 255, 255, 0.8)',
      animation: 'loading 1.5s infinite ease-in-out'
    },

    details: {
      background: colors.detailsBg,
      borderBottom: `1px solid ${colors.detailsBorder}`,
      padding: '12px 16px',
      fontSize: '0.85rem',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
    },

    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '4px 0',
      color: isDark ? '#D1D5DB' : '#6B7280'
    },

    detailLabel: {
      fontWeight: 500
    },

    detailValue: {
      fontWeight: 600
    },

    errorRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 0',
      color: '#EF4444'
    },

    errorIcon: {
      fontSize: '1rem'
    },

    errorMessage: {
      fontSize: '0.85rem',
      fontWeight: 500
    },

    hint: {
      marginTop: '8px',
      padding: '8px 12px',
      background: isDark ? '#374151' : '#F3F4F6',
      borderRadius: '6px',
      fontSize: '0.8rem',
      color: isDark ? '#9CA3AF' : '#6B7280',
      lineHeight: 1.4
    }
  };
}

// CSS Keyframes (werden per Style-Tag eingefügt)
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes loading {
    0% { transform: translateX(-100%); }
    50% { transform: translateX(200%); }
    100% { transform: translateX(400%); }
  }
`;
if (!document.head.querySelector('[data-offline-indicator-styles]')) {
  styleSheet.setAttribute('data-offline-indicator-styles', 'true');
  document.head.appendChild(styleSheet);
}

export default OfflineIndicator;
